// CAPABILITY: Chat composer — pill input + live char counter + emoji + send.
// Enforces max length client-side (server is authoritative). Shows sender-only
// moderation notices returned by the send call.
import React, { useRef, useState } from "react";
import { MESSAGE_WARN_RATIO } from "../chatConfig";
import { sanitizeChatText } from "../utils/sanitizeChatText";

const QUICK_EMOJIS = [
  "😀", "😂", "😍", "👍", "🙏", "🔥", "🎉", "👏",
  "❤️", "😮", "😢", "😎", "🤔", "👀", "💯", "🚀",
];

export default function ChatComposer({ maxChars = 500, sending, onSend, accentColor, accentText }) {
  const [value, setValue] = useState("");
  const [notice, setNotice] = useState(null); // { type: "blocked"|"info"|"error", text }
  const [showEmoji, setShowEmoji] = useState(false);
  const inputRef = useRef(null);

  const used = value.length;
  const warn = used >= Math.floor(maxChars * MESSAGE_WARN_RATIO);
  const over = used > maxChars;
  const canSend = used > 0 && !over && !sending;

  const insertEmoji = (emoji) => {
    setValue((prev) => (prev + emoji).slice(0, maxChars));
    setShowEmoji(false);
    inputRef.current?.focus();
  };

  const submit = async (e) => {
    e?.preventDefault();
    const clean = sanitizeChatText(value, { maxChars });
    if (!clean || sending) return;
    setNotice(null);

    const result = await onSend(clean);
    if (result?.status_label === "needs_name") {
      setNotice({ type: "info", text: "Choose a display name, then press Send." });
      return;
    }
    if (result?.ok && result?.status_label === "published") {
      setValue("");
    } else if (result?.status_label === "blocked") {
      setNotice({
        type: "blocked",
        text:
          result?.reason_message ||
          "Your message wasn't posted: it didn't meet this event's chat standards.",
      });
      setValue("");
    } else if (!result?.ok) {
      setNotice({ type: "error", text: "Couldn't send. Check your connection and try again." });
    }
  };

  return (
    <div className="border-t border-slate-200 bg-white">
      {notice && (
        <div
          className={`px-3 py-2 text-xs font-medium ${
            notice.type === "blocked"
              ? "bg-amber-50 text-amber-800"
              : notice.type === "info"
              ? "bg-sky-50 text-sky-800"
              : "bg-rose-50 text-rose-700"
          }`}
        >
          {notice.text}
        </div>
      )}

      {showEmoji && (
        <div className="grid grid-cols-8 gap-1 border-b border-slate-100 bg-slate-50 p-2">
          {QUICK_EMOJIS.map((emoji) => (
            <button
              key={emoji}
              type="button"
              className="rounded-md p-1 text-lg transition hover:bg-slate-200"
              onClick={() => insertEmoji(emoji)}
            >
              {emoji}
            </button>
          ))}
        </div>
      )}

      <form onSubmit={submit} className="flex items-end gap-2 p-2.5">
        <button
          type="button"
          onClick={() => setShowEmoji((s) => !s)}
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-lg transition hover:bg-slate-100 ${
            showEmoji ? "bg-slate-100" : ""
          }`}
          aria-label="Insert emoji"
          title="Emoji"
        >
          😊
        </button>

        <div className="relative flex-1">
          <textarea
            ref={inputRef}
            value={value}
            onChange={(e) => setValue(e.target.value.slice(0, maxChars + 50))}
            onKeyDown={(e) => {
              if (e.key === "Enter" && !e.shiftKey) submit(e);
            }}
            rows={1}
            placeholder="Type a message…"
            className="block max-h-28 min-h-[40px] w-full resize-none rounded-md border border-slate-200 bg-slate-50 px-3.5 py-2.5 pr-12 text-sm leading-snug text-slate-800 outline-none transition placeholder:text-slate-400 focus:border-slate-300 focus:bg-white"
          />
          <span
            className={`pointer-events-none absolute bottom-2 right-3 text-[10px] tabular-nums ${
              over ? "text-rose-500" : warn ? "text-amber-500" : "text-slate-300"
            }`}
          >
            {used}/{maxChars}
          </span>
        </div>

        <button
          type="submit"
          disabled={!canSend}
          className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md shadow-sm transition hover:brightness-110 disabled:cursor-not-allowed disabled:opacity-40"
          style={{ backgroundColor: accentColor || "#0f172a", color: accentText || "#ffffff" }}
          aria-label="Send message"
        >
          {sending ? (
            <span className="text-base leading-none">…</span>
          ) : (
            <svg
              viewBox="0 0 24 24"
              className="h-[18px] w-[18px]"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M22 2 11 13" />
              <path d="M22 2 15 22 11 13 2 9 22 2Z" />
            </svg>
          )}
        </button>
      </form>
    </div>
  );
}
